import React, { useState } from "react";
import CommandePage from "../../core/commande/CommandePage";
import PatientForm from "../../components/PatientForm";
import { dashboardContext } from "./dashboard.context";
import { API } from "../../services/api";

function mergeValidationState(previous, response) {
    if (!previous || !response) return previous;
    const patients = new Map((response.patients || []).map(patient => [patient.commandePatientId, patient]));
    return {
        ...previous,
        managementStatut: response.managementStatut ?? previous.managementStatut,
        statut: response.statut ?? previous.statut,
        patients: (previous.patients || []).map(patient => {
            const fresh = patients.get(patient.commandePatientId);
            if (!fresh) return patient;
            const products = new Map((fresh.produits || []).map(product => [product.id, product]));
            return {
                ...patient,
                managementValidated: fresh.managementValidated,
                managementValidatedAt: fresh.managementValidatedAt,
                managementValidatedBy: fresh.managementValidatedBy,
                pharmaceuticalValidated: fresh.pharmaceuticalValidated,
                produits: (patient.produits || []).map(product => ({ ...product, ...(products.get(product.id) || {}) })),
            };
        }),
    };
}

function samePatientIdentity(left, right) {
    const normalize = value => String(value || "").trim().toLowerCase();
    return normalize(left?.nom) === normalize(right?.nom)
        && normalize(left?.prenom) === normalize(right?.prenom)
        && (left?.dateNaissance ?? null) === (right?.dateNaissance ?? null);
}

function findSavedPatient(patients, original) {
    if (original?.commandePatientId != null) {
        const byLink = (patients || []).find(patient => patient.commandePatientId === original.commandePatientId);
        if (byLink) return byLink;
    }
    return (patients || []).find(patient => samePatientIdentity(patient, original)) || null;
}

function ManagementEditor({ draft, setDraft, save, isDirty }) {
    const [selection, setSelection] = useState({ active: false, ids: new Set(), loading: false });

    const applyResponse = response => setDraft(previous => mergeValidationState(previous, response.data));
    const ensureSaved = async () => !isDirty || await save();

    const validatePatient = async patient => {
        let savedPatient = patient;
        if (isDirty) {
            if (!await save()) return false;
            const fresh = await API.get(`/commandes/${draft.id}`, {
                params: { _ts: Date.now() },
                headers: { "Cache-Control": "no-cache" },
            });
            savedPatient = findSavedPatient(fresh.data?.patients, patient);
            if (!savedPatient?.commandePatientId) return false;
        }
        const response = await API.post(
            `/commandes/${draft.id}/patients/${savedPatient.commandePatientId}/validation-gestion`,
            null,
            { noRedirectOn401: true }
        );
        applyResponse(response);
        return true;
    };

    const toggleProduct = id => {
        if (id == null) return;
        setSelection(previous => {
            const ids = new Set(previous.ids);
            if (ids.has(id)) ids.delete(id); else ids.add(id);
            return { ...previous, ids };
        });
    };

    const validateProducts = async () => {
        if (!selection.active) {
            if (!await ensureSaved()) return;
            setSelection({ active: true, ids: new Set(), loading: false });
            return;
        }
        if (selection.ids.size === 0) return;
        setSelection(previous => ({ ...previous, loading: true }));
        try {
            const response = await API.post(
                `/commandes/${draft.id}/produits/validation-gestion`,
                { medicamentIds: [...selection.ids] },
                { noRedirectOn401: true }
            );
            applyResponse(response);
            setSelection({ active: false, ids: new Set(), loading: false });
        } finally {
            setSelection(previous => ({ ...previous, loading: false }));
        }
    };

    const productAction = (
        <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
            {selection.active && (
                <button type="button" onClick={() => setSelection({ active: false, ids: new Set(), loading: false })}>
                    Annuler
                </button>
            )}
            <button
                type="button"
                onClick={validateProducts}
                disabled={selection.loading || (selection.active && selection.ids.size === 0)}
                style={{ padding: "10px 16px", border: 0, borderRadius: "8px", background: "#0C3B3C", color: "white", fontWeight: 700 }}
            >
                {selection.loading ? "Validation..." : selection.active ? `Valider les ${selection.ids.size} produits` : "Valider des produits"}
            </button>
        </div>
    );

    return (
        <PatientForm
            patients={draft?.patients || []}
            ehpadId={draft?.ehpadId}
            medicamentStrategy={dashboardContext.medicamentStrategy}
            onChange={patients => setDraft(previous => ({ ...previous, patients }))}
            onSave={save}
            isDirty={isDirty}
            onValidatePatient={validatePatient}
            patientValidationField="managementValidated"
            patientValidationLabel="Valider la saisie"
            productSelectionMode={selection.active}
            selectedProductIds={selection.ids}
            onToggleProductSelection={toggleProduct}
            productValidationAction={productAction}
        />
    );
}

export default function Dashboard() {
    return (
        <CommandePage
            context={dashboardContext}
            renderDetailActions={({ commande, saveDraft, isDirty, goToList, refresh }) => (
                commande.managementStatut !== "VALIDE" ? (
                    <button
                        type="button"
                        onClick={async () => {
                            if (isDirty && !await saveDraft()) return;
                            await API.post(`/commandes/${commande.id}/validation-gestion`, null, { noRedirectOn401: true });
                            goToList();
                            refresh();
                        }}
                        style={{ padding: "7px 14px", border: 0, borderRadius: "7px", background: "#17B978", color: "#122A26", fontWeight: 800 }}
                    >
                        Valider toute la saisie
                    </button>
                ) : null
            )}
        >
            {({ draft, setDraft, save, isDirty }) => (
                <ManagementEditor draft={draft} setDraft={setDraft} save={save} isDirty={isDirty} />
            )}
        </CommandePage>
    );
}
